"use client";

import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/design-system/input";

export type PendingAccountLink = {
  targetEmail: string;
  organizationCount: number;
};

type Step = "offer" | "pin" | "success";

type ActionBody = {
  action: "request" | "confirm" | "dismiss";
  pin?: string;
};

async function postAccountLinkAction(body: ActionBody): Promise<{
  ok: boolean;
  message?: string;
  resendAvailableAt?: string;
}> {
  const response = await fetch("/api/discovery/account-link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as {
    message?: string;
    resendAvailableAt?: string;
  };
  return { ok: response.ok, ...data };
}

/**
 * Shown on the select-organization page when the discovery email has an
 * exact local-part counterpart on the legacy newform.ai domain. Linking here
 * (before any org membership exists) renames the old Stytch members in
 * place, so after a reload the user's organizations appear in discovery.
 */
export function LinkOldAccountCard({
  targetEmail,
  organizationCount,
}: PendingAccountLink) {
  const [step, setStep] = useState<Step>("offer");
  const [dismissed, setDismissed] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState<Date | null>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  useEffect(() => {
    if (!resendAvailableAt) {
      setResendSecondsLeft(0);
      return;
    }
    const update = () => {
      setResendSecondsLeft(
        Math.max(
          0,
          Math.ceil((resendAvailableAt.getTime() - Date.now()) / 1000),
        ),
      );
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [resendAvailableAt]);

  const handleSendCode = async () => {
    setBusy(true);
    setError(null);
    const result = await postAccountLinkAction({ action: "request" });
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? "Something went wrong — try again");
      return;
    }
    if (result.resendAvailableAt) {
      setResendAvailableAt(new Date(result.resendAvailableAt));
    }
    setPin("");
    setStep("pin");
  };

  const handleConfirm = async () => {
    if (!/^\d{6}$/.test(pin)) {
      return;
    }
    setBusy(true);
    setError(null);
    const result = await postAccountLinkAction({ action: "confirm", pin });
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? "Something went wrong — try again");
      return;
    }
    setStep("success");
    // Reload so the server component re-lists discovered organizations —
    // the freshly renamed memberships now belong to this email.
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const handleDismiss = async () => {
    setDismissed(true);
    await postAccountLinkAction({ action: "dismiss" });
  };

  if (dismissed) {
    return null;
  }

  const orgLabel = organizationCount === 1 ? "workspace" : "workspaces";

  return (
    <div className="rounded-nf-ds-2xl bg-card border border-nf-ds-purple-product/40 p-nf-ds-6 shadow-nf-ds-xs mb-nf-ds-6">
      <span className="font-nf-ds-mono text-[9px] uppercase tracking-[0.12em] text-nf-ds-purple-product">
        Account found
      </span>

      {step === "offer" && (
        <>
          <h2 className="font-nf-ds-serif text-[22px] leading-[1.15] text-foreground mt-nf-ds-2">
            Link your old account?
          </h2>
          <p className="font-nf-ds-sans text-[14px] text-muted-foreground mt-nf-ds-2">
            We found an existing account for{" "}
            <span className="text-foreground font-[500]">{targetEmail}</span>{" "}
            with {organizationCount} {orgLabel}. Link it to keep your history —
            we&apos;ll send a confirmation code to {targetEmail}. After linking,
            that address will no longer sign in.
          </p>
          {error ? (
            <p className="font-nf-ds-sans text-[13px] text-red-600 mt-nf-ds-3">
              {error}
            </p>
          ) : null}
          <div className="flex items-center gap-nf-ds-4 mt-nf-ds-5">
            <button
              type="button"
              onClick={handleSendCode}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-nf-ds-md bg-nf-ds-purple-product text-nf-ds-white font-nf-ds-sans text-[14px] font-[500] px-nf-ds-5 h-10 hover:bg-nf-ds-purple-700 disabled:opacity-50 cursor-pointer"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Sending…" : "Send code"}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="font-nf-ds-sans text-[13px] text-nf-ds-gray-500 hover:text-nf-ds-text-strong underline underline-offset-2 cursor-pointer"
            >
              This is a new account
            </button>
          </div>
        </>
      )}

      {step === "pin" && (
        <>
          <h2 className="font-nf-ds-serif text-[22px] leading-[1.15] text-nf-ds-black mt-nf-ds-2">
            Enter your confirmation code
          </h2>
          <p className="font-nf-ds-sans text-[14px] text-nf-ds-gray-600 mt-nf-ds-2">
            We sent a 6-digit code to {targetEmail}. It expires in 10 minutes.
          </p>
          <div className="flex flex-col gap-nf-ds-3 mt-nf-ds-4 max-w-[280px]">
            <Input
              value={pin}
              onChange={(event) =>
                setPin(event.target.value.replace(/\D/g, "").slice(0, 6))
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void handleConfirm();
                }
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              className="text-center text-[20px] tracking-[0.3em] font-nf-ds-mono"
              aria-label="Confirmation code"
            />
            {error ? (
              <p className="font-nf-ds-sans text-[13px] text-red-600">
                {error}
              </p>
            ) : null}
            <button
              type="button"
              onClick={handleSendCode}
              disabled={busy || resendSecondsLeft > 0}
              className="self-start font-nf-ds-sans text-[13px] text-nf-ds-gray-500 hover:text-nf-ds-text-strong disabled:opacity-50 disabled:cursor-default cursor-pointer"
            >
              {resendSecondsLeft > 0
                ? `Resend code (${resendSecondsLeft}s)`
                : "Resend code"}
            </button>
          </div>
          <div className="mt-nf-ds-4">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy || !/^\d{6}$/.test(pin)}
              className="inline-flex items-center gap-2 rounded-nf-ds-md bg-nf-ds-purple-product text-nf-ds-white font-nf-ds-sans text-[14px] font-[500] px-nf-ds-5 h-10 hover:bg-nf-ds-purple-700 disabled:opacity-50 cursor-pointer"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Linking…" : "Link accounts"}
            </button>
          </div>
        </>
      )}

      {step === "success" && (
        <>
          <h2 className="font-nf-ds-serif text-[22px] leading-[1.15] text-nf-ds-black mt-nf-ds-2">
            Accounts linked
          </h2>
          <p className="font-nf-ds-sans text-[14px] text-nf-ds-gray-600 mt-nf-ds-2 flex items-center gap-nf-ds-2">
            <Loader2 className="h-4 w-4 animate-spin text-nf-ds-purple-product" />
            Loading your workspaces…
          </p>
        </>
      )}
    </div>
  );
}
